import { ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { Division, DivisionTree } from '@zencity/survey-types';

const flattenDivisionTreeChildren = (divisionTree: DivisionTree[]): Division[] =>
  divisionTree.reduce<Division[]>((accumulator, divisionTreeItem) => {
    const { children_divisions: children, ...division } = divisionTreeItem;
    accumulator.push(division);
    if (children.length > 0) {
      return [...accumulator, ...flattenDivisionTreeChildren(children)];
    }

    return accumulator;
  }, []);

export const divisionLabelForFilter = (divisionId: number, clientData: ReportBuilderClientData): string => {
  const selectedDivision = Object.values(clientData.divisionTreeBySurveyType)
    .map(flattenDivisionTreeChildren)
    .flat()
    .find((instance) => instance.id === divisionId);
  if (!selectedDivision) {
    return `MISSING Division of id #${divisionId}`;
  }

  return selectedDivision.geo_targeting_ref.polygon_name;
};

export const getAllDivisionsIdsOfDivisionTree = (divisionTree: DivisionTree[]): number[] =>
  divisionTree.reduce<number[]>((accumulator, divisionTreeItem) => {
    const { children_divisions: children, ...division } = divisionTreeItem;
    return [...accumulator, division.id, ...getAllDivisionsIdsOfDivisionTree(children)];
  }, []);

export const getChildrenDivisionsIds = (
  baseDivisionId: number,
  divisionTree: DivisionTree[],
  includeBaseDivision = false,
): number[] => {
  const relevantIds: number[] = includeBaseDivision ? [baseDivisionId] : [];

  return divisionTree.reduce<number[]>((accumulator, divisionTreeItem) => {
    const { children_divisions: children, ...division } = divisionTreeItem;
    if (division.id === baseDivisionId) {
      return [...accumulator, ...getAllDivisionsIdsOfDivisionTree(children)];
    }
    if (children.length > 0) {
      // Keep searching for the base ID inside the children.
      return [...accumulator, ...getChildrenDivisionsIds(baseDivisionId, children)];
    }

    return accumulator;
  }, relevantIds);
};

export const getSiblingsDivisionsTreeIds = (baseDivisionId: number, divisionTree: DivisionTree[]): number[] =>
  divisionTree.reduce<number[]>((accumulator, divisionTreeItem, index, siblingsTree) => {
    if (accumulator.length) {
      return accumulator;
    }
    if (divisionTreeItem.id === baseDivisionId) {
      // Found the division, now return all IDs of each sibling's tree.
      return getAllDivisionsIdsOfDivisionTree(siblingsTree);
    }
    const { children_divisions: children } = divisionTreeItem;
    if (children.length > 0) {
      // Keep searching for the base ID inside the children.
      return getSiblingsDivisionsTreeIds(baseDivisionId, children);
    }

    return accumulator;
  }, []);

export const getParentDivision = (divisionId: number, divisionTree: DivisionTree[]): Division | undefined =>
  divisionTree.reduce<Division | undefined>((accumulator, divisionTreeItem) => {
    if (accumulator) {
      // Parent division already found in a previous iteration, avoid further
      // searching which may end up returning `undefined` (due to the
      // recursion) and simply return it.
      return accumulator;
    }

    const { children_divisions: children, ...potentialParentDivision } = divisionTreeItem;
    if (potentialParentDivision.id === divisionId) {
      // At this point, we got too deep, and if a parent wasn't found, then the
      // given division is actually the root division in the given tree.
      return accumulator;
    }

    const lookupDivision = children.find((division) => division.id === divisionId);
    if (lookupDivision) {
      // Found the division to lookup within the children, thus, the potential
      // parent is the actual parent.
      return potentialParentDivision;
    }

    return getParentDivision(divisionId, children);
  }, undefined);

export const getDivision = (divisionId: number, divisionTree: DivisionTree[]): DivisionTree | undefined =>
  divisionTree.reduce<DivisionTree | undefined>((accumulator, divisionTreeItem) => {
    if (accumulator) {
      // Division already found in a previous iteration, avoid further
      // searching which may end up returning `undefined` (due to the
      // recursion) and simply return it.
      return accumulator;
    }

    if (divisionTreeItem.id === divisionId) {
      return divisionTreeItem;
    }

    const { children_divisions: children } = divisionTreeItem;

    const lookupDivision = children.find((division) => division.id === divisionId);
    if (lookupDivision) {
      // Found the division to lookup within the children.
      return lookupDivision;
    }

    return getDivision(divisionId, children);
  }, undefined);

const SENTINEL_LEVEL = -1;
const getMostGranularLevelConsideringInitial = (divisionTree: DivisionTree[]): number =>
  divisionTree.reduce<number>((accumulator, divisionTreeItem) => {
    const { children_divisions: children } = divisionTreeItem;
    if (children?.length > 0) {
      return getMostGranularLevelConsideringInitial(children);
    }
    return Math.max(accumulator, divisionTreeItem.depth);
  }, SENTINEL_LEVEL);

export const getMostGranularLevel = (divisionTree: DivisionTree[]): number | undefined => {
  // Using a helper function with a sentinel, so it'll be easy to utilize
  // `Math.max()` in there.
  const level = getMostGranularLevelConsideringInitial(divisionTree);
  return level !== SENTINEL_LEVEL ? level : undefined;
};
